import type { ReactNode } from "react";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/ui";

/**
 * SFCC-11498 (ARTICLES-12) — port of composable's `PaginatedGridDisplay`.
 * Responsive tile grid + "Showing X of Y" count + Load More / View All controls.
 * Shared by the custom and dynamic `ArticleGrid` modes.
 */

interface PaginatedGridDisplayProps {
  items: ReactNode[];
  itemsText?: string;
  onShowMore?: () => void;
  onShowAll?: () => void;
  allEntriesDisplayed: boolean;
  /** noWrap attributes injected by Builder; spread on the root element. */
  attributes?: Record<string, unknown>;
}

export function PaginatedGridDisplay({
  items,
  itemsText,
  onShowMore,
  onShowAll,
  allEntriesDisplayed,
  attributes,
}: PaginatedGridDisplayProps) {
  const { className, ...rest } = (attributes ?? {}) as {
    className?: string;
    [key: string]: unknown;
  };

  return (
    <div className={cn("mx-auto flex flex-col", className)} {...rest}>
      <div
        className="grid auto-rows-auto grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3 md:gap-6 xl:grid-cols-4"
        style={{ gridAutoFlow: "dense" }}
      >
        {items}
      </div>

      {itemsText && (
        <div className="mx-auto mb-8 mt-[100px] text-black">
          <span
            className="inline text-sm font-medium"
            data-testid="articleGrid-Count"
          >
            {itemsText}
          </span>
        </div>
      )}

      {!allEntriesDisplayed && onShowMore && (
        <Button
          type="button"
          variant="cta"
          onClick={onShowMore}
          className="mx-auto w-[12.5rem]"
          data-testid="articleGrid-ShowMore"
        >
          Load More
        </Button>
      )}

      {!allEntriesDisplayed && onShowAll && (
        <Button
          type="button"
          variant="link"
          onClick={onShowAll}
          className="mx-auto mt-8 w-[12.5rem]"
          data-testid="articleGrid-ShowAll"
        >
          View All
        </Button>
      )}
    </div>
  );
}

export default PaginatedGridDisplay;
